import React, { Fragment, useEffect, useState } from 'react';
import { Grid, Card, Toolbar, CardContent, Dialog, DialogContent, DialogActions, Typography, Button, LinearProgress } from '@mui/material';
import Config from '../../config';
import axios from 'axios';
import EnhancedTable from '../../components/table';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import DownloadButton from '../../components/download_button';
import TextField from '../../components/text_field';
import { formatAddress, getCompanyNames } from '../../helpers';
import Auth from '../../auth';

// react-final-form's Form/Field generics don't line up with this file's loose
// value shapes; cast to any at the import boundary (same pattern as
// reports.tsx/scorecards.tsx/search.tsx).
const Form: any = FormBase;
const Field: any = FieldBase;

type AddressRecord = {
    Id?: number;
    PartnerCoId?: number | null;
    [key: string]: any;
};

type DefaultAddressesProps = {
    setTitle?: (title: string, flag?: boolean) => void;
    addressType: string;
    addressTitle: string;
};

const DefaultAddresses = (props: DefaultAddressesProps) => {
    const { setTitle, addressType, addressTitle } = props;

    const [showEdit, setShowEdit] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [defaultAddress, setDefaultAddress] = useState<AddressRecord | undefined>(undefined);
    const [disableEdit, setDisableEdit] = useState<boolean | undefined>(undefined);
    const [refresh, setRefresh] = useState<boolean | undefined>(undefined);
    const [editAddress, setEditAddress] = useState<AddressRecord | null | undefined>(undefined);
    const [disableConfirm, setDisableConfirm] = useState<boolean | undefined>(undefined);

    const loadDefaultAddress = async () => {
        var addresses = (await axios.get(Config.api + `/odata/Company/DefaultAddresses?$filter=AddressType eq '${addressType}'`)).data.value;
        var addr = addresses.find((r: AddressRecord) => r.PartnerCoId === null);
        addr = addr || {};
        setDefaultAddress(addr);
    };

    useEffect(() => {
        (async () => {
            if(setTitle) {
                switch(addressType) {
                    case 'RemitTo':
                        setTitle('Remittance Addresses', true);
                        break;
                    case 'Return':
                        setTitle('Return Addresses', true);
                        break;
                    case 'ShipFrom':
                        setTitle('Ship From Addresses', true);
                        break;
                    default:
                        setTitle('Default Addresses', true);
                }
            }
            await loadDefaultAddress();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getTableData = async () => {
        var partners = (await axios.get(Config.api + `/api/v1/partners`)).data.Body.Partners;
        var addresses = (await axios.get(Config.api + `/odata/Company/DefaultAddresses?$filter=AddressType eq '${addressType}'`)).data.value;
        return partners.map((row: any) => {
            var address = addresses.find((r: AddressRecord) => r.PartnerCoId === row.Id);
            address = address || {};
            var ret: AddressRecord = {PartnerName: row.CompanyName, AccountNumber: row.Id};
            Object.assign(ret, address);
            return ret;
        });
    };

    const saveDefaultAddress = async (e: AddressRecord) => {
        setDisableEdit(true);
        e.AddressType = addressType;
        await axios.put(Config.api + '/odata/Company/DefaultAddresses', e);
        if(e.PartnerCoId === null || e.PartnerCoId === undefined){
            await loadDefaultAddress();
        }else{
            setRefresh(prev => !prev);
            setShowEdit(false);
        }
    };

    const loadAddress = async (id?: number, partner?: any) => {
        setShowEdit(true);
        setEditAddress(null);
        setDisableEdit(false);
        if(id || id === 0){
            var addr = (await axios.get(Config.api + `/odata/Company/DefaultAddresses?$filter=Id eq ${id}`)).data.value[0];
            setEditAddress(addr);
        }else{
            setEditAddress({PartnerCoId: partner});
        }
    };

    const deleteAddress = async () => {
        setDisableConfirm(true);
        await axios.delete(Config.api + `/odata/Company/DefaultAddresses(${editAddress!.Id})`);
        setShowConfirm(false);
        setRefresh(prev => !prev);
    };

    const getAddressEditor = (disabled?: boolean) => {
        return <Fragment>
        <div style={{display: 'flex'}}>
          <Field style={{flex: 1}} component={TextField} disabled={disabled} label='Company Name' name='CompanyName'/>
        </div>
        <div style={{display: 'flex'}}>
          <Field style={{flex: 1, marginRight: '1em'}} component={TextField} disabled={disabled} label='First Name' name='FirstName'/>
          <Field style={{flex: 1}} component={TextField} disabled={disabled} label='Last Name' name='LastName'/>
        </div>
        <div style={{display: 'flex'}}>
          <Field style={{flex: 1, marginRight: '1em'}} component={TextField} disabled={disabled} label='Address Line 1' name='Address1'/>
          <Field style={{flex: 1}} component={TextField} disabled={disabled} label='Address Line 2' name='Address2'/>
        </div>
        <div style={{display: 'flex'}}>
          <Field style={{flex: 1, marginRight: '1em'}} component={TextField} disabled={disabled} label='City' name='City'/>
          <Field style={{flex: 1, marginRight: '1em'}} component={TextField} disabled={disabled} label='State' name='State'/>
          <Field style={{flex: 1, marginRight: '1em'}} component={TextField} disabled={disabled} label='ZIP Code' name='Zip'/>
          <Field style={{flex: 1}} component={TextField} disabled={disabled} label='Country' name='Country'/>
        </div>
        <div style={{display: 'flex', marginBottom: '.5em'}}>
          <Field style={{flex: 1, marginRight: '1em'}} component={TextField} disabled={disabled} label='Email' name='EMail'/>
          <Field style={{flex: 1, marginRight: '1em'}} component={TextField} disabled={disabled} label='Phone' name='Phone'/>
          <Field style={{flex: 1}} component={TextField} disabled={disabled} label='Address Code' name='AddressCode'/>
        </div>
        </Fragment>;
    };

    let canEdit = Auth.hasPermission('settings/manage');
    var config = {
        getData: getTableData,
        columns: [
          { id: "AccountNumber", filterable: true, sortable: true, label: 'Account Number', width: '13em' },
          { id: 'PartnerName', filterable: true, sortable: true, label: 'Company Name', width: '25em', getOptions: getCompanyNames },
          { id: 'Address1', sortable: true, label: 'Address', template: (value: any, row: any) => formatAddress(row) },
          { command: 'commands', stopPropagation: 'true', width: '20em', template: (value: any, row: any) => <Fragment>
              <Button style={{marginRight: '1rem'}} variant='outlined' size='small' disabled={!canEdit} onClick={() => loadAddress(row.Id, row.AccountNumber)}>Edit</Button>
              <Button variant='outlined' size='small' style={{visibility: row.Id ? 'visible' : 'hidden'}} disabled={!canEdit} onClick={() => { setEditAddress(row); setShowConfirm(true); setDisableConfirm(false); }}>Delete</Button>
          </Fragment>}
      ],
      order: 'asc',
      orderBy: 'PartnerName',
      keyField: 'AccountNumber',
      pageSize: 20,
      pageSizes: [10, 20, 50, 100],
      refresh: refresh,
      title: `Partner Specific ${addressTitle} Addresses`
    };
    return (
        <Grid container spacing={2}>
            <Grid size={{ md: 12 }}>
                <Card>
                    <Toolbar className='lbtoolbar'>{`Default ${addressTitle} Address`}</Toolbar>
                        <CardContent>
                            <Form onSubmit={saveDefaultAddress} initialValues={defaultAddress} render={({ handleSubmit, pristine, invalid }: any) => {
                                  return (
                                      <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', maxWidth: '900px'}}>
                                      {getAddressEditor(!canEdit)}
                                       <div style={{textAlign: 'left', marginBottom: '.5em'}}>
                                        <DownloadButton onClick={handleSubmit} disabled={pristine || !canEdit} variant='contained' color='primary'>Save</DownloadButton>
                                       </div>
                                      </form>
                                  );}}/>
                        </CardContent>
                </Card>
            </Grid>
            <Grid size={{ md: 12 }}>
                <EnhancedTable config={config}/>
            </Grid>
            <Dialog
                maxWidth="sm"
                aria-labelledby="confirmation-dialog-title"
                open={showConfirm}
                onClose={(event: any, reason: string) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (disableConfirm) return;
                  }
                  setShowConfirm(false);
                }}>
                <Toolbar className='lbtoolbar'>Confirm Delete</Toolbar>
                <DialogContent>
                  <Typography>Are you sure you want to delete this address?</Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowConfirm(false)} disabled={disableConfirm} color="primary">
                    No
                  </Button>
                  <Button onClick={deleteAddress} disabled={disableConfirm} color="primary">
                    Yes
                  </Button>
                </DialogActions>
              </Dialog>
            <Dialog
                maxWidth="sm"
                open={showEdit}
                onClose={(event: any, reason: string) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (disableEdit) return;
                  }
                  setShowEdit(false);
                }}
                fullWidth={true}>
                {editAddress ?
                    <Form onSubmit={saveDefaultAddress}
                    initialValues={editAddress}
                    render={({ handleSubmit, pristine, invalid, values }: any) => (
                      <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
                      <Toolbar className='lbtoolbar'>Edit Address</Toolbar>
                    <DialogContent>
                    <div style={{display: 'flex', flexDirection: 'column', flexShrink: '0'}}>
                        {getAddressEditor(!canEdit)}
                      </div>
                    </DialogContent>
                    <DialogActions>
                      <Button type='submit' disabled={pristine || invalid || disableEdit} color="primary">
                        Save
                      </Button>
                    </DialogActions>
                    </form>
                    )}/>
                     :  <DialogContent><LinearProgress style={{height: '2em'}} variant="indeterminate"/></DialogContent> }

              </Dialog>
        </Grid>
    );
};

export default DefaultAddresses;
